import { memo, useCallback } from 'react';

import {
  useTrackToggledAnonymousAccountRequirementOff,
  useTrackToggledAnonymousAccountRequirementOn,
} from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';

import { type ShareUrlResponseWithSource } from '~/components/SharePrivateClipModal/types';
import { UpsellFlashIconButton } from '~/components/SharePrivateClipModalV2/UpsellFlashIconButton';
import { PermissionToggleInfo } from '~/components/SharePrivateContentModal/shared/PermissionToggleInfo';
import { SharePrivateContentPermissionToggle } from '~/components/SharePrivateContentModal/shared/SharePrivateContentPermissionToggle';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useShareSettings } from '~/hooks/useShareSettings';
import { type UpdateShortIdParams } from '~/swr-hooks/shortUrl/useUpdateShortId';

type ShareRequireAccountSettingProps = {
  shortIdInfo: ShortUrlResponse;
  isLoading: boolean;
  onShortUrlUpdate: (
    props: DeepPartial<Omit<UpdateShortIdParams, 'id' | 'objectId' | 'boardContext' | 'shortId'>>,
  ) => Promise<ShareUrlResponseWithSource>;
};

export const ShareRequireAccountSetting = memo(
  ({ shortIdInfo, isLoading, onShortUrlUpdate }: ShareRequireAccountSettingProps) => {
    const { requireAccountEnabled } = useShareSettings();
    const { trackToggledAnonymousAccountRequirementOn } = useTrackToggledAnonymousAccountRequirementOn();
    const { trackToggledAnonymousAccountRequirementOff } = useTrackToggledAnonymousAccountRequirementOff();

    const onCheckedChange = useCallback(
      async (checked: boolean) => {
        const response = await onShortUrlUpdate({
          requireAccount: checked,
        });

        if (checked) {
          trackToggledAnonymousAccountRequirementOn({
            share_url: response.id,
            object_id: response.objectId,
            object_type: response.objectType,
          });
        } else {
          trackToggledAnonymousAccountRequirementOff({
            share_url: response.id,
            object_id: response.objectId,
            object_type: response.objectType,
          });
        }
      },
      [onShortUrlUpdate, trackToggledAnonymousAccountRequirementOff, trackToggledAnonymousAccountRequirementOn],
    );

    return (
      <SharePrivateContentPermissionToggle
        label="Require name and email to view"
        id="requireAccount"
        isLoading={isLoading}
        checked={!!shortIdInfo.requireAccount}
        disabled={!requireAccountEnabled}
        onCheckedChange={onCheckedChange}
      >
        {!requireAccountEnabled ? (
          <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>
        ) : (
          <PermissionToggleInfo text="Link visitors must enter name and email to view" />
        )}
      </SharePrivateContentPermissionToggle>
    );
  },
);

ShareRequireAccountSetting.displayName = 'ShareRequireAccountSetting';
